import {
  FieldMappingFieldItem,
  FieldMappingVariables,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';

export type MappedDefaultValue = {
  value: string;
  variables?: FieldMappingVariables;
};

export type BuilderProps = {
  canRemove?: boolean;
  canEditKind?: boolean | FieldTypeKind[];
  canEditRequired?: boolean;
  canEditOptions?: boolean;
  canEditConditions?: boolean;
  canReorder?: boolean;
  canDuplicate?: boolean;
  canEditFilesMultiple?: boolean;
  canEditDefaultValue?: boolean;
};

export type FieldId = string;

export type FieldOption = {
  value: string;
  label: string;
  // Present on options sourced from a company list; carried through so the data
  // round-trips unchanged from when the API inlined options.
  shortId?: string;
};

export type SubmitFieldType = {
  kind: 'submit';
  id: FieldId;
  builderProps?: BuilderProps;

  label: string;
  saveLabel?: string; // Optional for backward compatibility
  hideSaveButton?: boolean;
};

export type HeadingFieldType = {
  kind: 'heading';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  label: string | EditorValue<{ variables?: FieldMappingVariables }>;
};

export type SubheadingFieldType = {
  kind: 'subheading';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  label: string | EditorValue<{ variables?: FieldMappingVariables }>;
};

export type HtmlFieldType = {
  kind: 'html';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  content: string | EditorValue<{ variables?: FieldMappingVariables }>;
};

export type ImageFieldType = {
  kind: 'image';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  imageWidth: number;
  url: string;
  imageId?: string;
};

export type VideoFieldType = {
  kind: 'video';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  url: string;
};

export type ContentFileFieldType = {
  kind: 'content-file';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  label: string;
  fileAlias: string;
  url: string;
  urlConverted?: string;
  fileName: string;
  fileId?: string;
  mappedDefaultValue?: MappedDefaultValue;
};

export type SeparatorFieldType = {
  kind: 'separator';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;
};

export type SectionFieldType = {
  kind: 'section';
  id: FieldId;
  condition: Condition;
  builderProps?: BuilderProps;

  label: string;
  columns: 1 | 2;
  fields: Array<FieldType>;
};

export type PhoneFieldType = {
  kind: 'input-phone';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  isRequired: boolean;
  defaultValue?: string;
  mappedDefaultValue?: MappedDefaultValue;

  value: string;
};

export type EmailFieldType = {
  kind: 'input-email';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  confirmLabel: string;
  defaultValue?: string;
  placeholder: string;
  mappedDefaultValue?: MappedDefaultValue;

  isRequired: boolean;
  hasConfirmation: boolean;
  isReadOnly?: boolean;

  value: string;
};

export type SmallInputFieldType = {
  kind: 'input-text-small';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  placeholder: string;
  isRequired: boolean;
  defaultValue?: string;
  mappedDefaultValue?: MappedDefaultValue;
  isReadOnly?: boolean;

  value: string;
};

export type LargeInputFieldType = {
  kind: 'input-text-large';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  placeholder: string;
  isRequired: boolean;
  defaultValue?: string;
  mappedDefaultValue?: MappedDefaultValue;
  isReadOnly?: boolean;

  value: string;
};
export type KennitalaFieldType = {
  kind: 'input-kennitala';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  placeholder: string;
  isRequired: boolean;
  defaultValue?: string;
  mappedDefaultValue?: MappedDefaultValue;

  value: string;
};

export type NumberFieldType = {
  kind: 'input-number';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  placeholder: string;
  isRequired: boolean;
  defaultValue?: number | null;
  mappedDefaultValue?: MappedDefaultValue;

  isDecimal: boolean;
  restrictMin: boolean;
  min: number | null;
  restrictMax: boolean;
  max: number | null;
  allowNegative: boolean;

  value: number | null;
};

export type DateRestrictionType = 'none' | 'disablePast' | 'disableFuture' | 'range';

export type DateFieldType = {
  kind: 'input-date';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  placeholder: string;
  isRequired: boolean;
  defaultValue?: string;
  mappedDefaultValue?: MappedDefaultValue;

  dateRestrictionType: DateRestrictionType;
  dateRangeStart?: string;
  dateRangeEnd?: string;

  value: string;
};

export type FileFieldType = {
  kind: 'input-file';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  placeholder: string;
  isRequired: boolean;
  restrictTypes: boolean;
  mappedDefaultValue?: MappedDefaultValue;

  /**
   * @deprecated Use `allowMultiple` instead.
   */
  maximumFileCount?: number;
  allowMultiple?: boolean;
  accept: string;

  value: (File | number)[];
};

export type SingleChoiceFieldType = {
  kind: 'input-single-choice';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  options: FieldOption[];
  optionsFromListId: string | null;
  excludedOptions: string[] | null;
  defaultValue?: string | null;
  mappedDefaultValue?: MappedDefaultValue;

  isRequired: boolean;

  value: string | null;
};

export type MultipleChoiceFieldType = {
  kind: 'input-multiple-choice';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  label: string;
  description?: string;
  options: FieldOption[];
  optionsFromListId: string | null;
  excludedOptions: string[] | null;
  defaultValue?: string[];
  mappedDefaultValue?: MappedDefaultValue;

  isRequired: boolean;

  value: string[];
};

export type DropdownFieldType = {
  kind: 'input-dropdown';
  id: FieldId;
  condition: Condition;
  journeyAttributeId?: string;
  companyAttributeId?: string;
  builderProps?: BuilderProps;

  options: FieldOption[];
  optionsFromListId: string | null;
  excludedOptions: string[] | null;
  label: string;
  description?: string;
  defaultValue?: string | null;
  mappedDefaultValue?: MappedDefaultValue;

  isRequired: boolean;
  placeholder: string;

  value: string | null;
};

export type FormFieldValue = string | number | boolean | Array<string | number>;

export type FormFile = {
  name: string;
  src: string;
};

export type ConditionComparator =
  | 'is'
  | 'not_is'
  | 'any_of'
  | 'none_of'
  | 'before'
  | 'on'
  | 'after'
  | 'starts'
  | 'not_starts'
  | 'contains'
  | 'not_contains';

const NEGATIVE_COMPARATORS: ReadonlySet<ConditionComparator> = new Set([
  'not_is',
  'none_of',
  'not_starts',
  'not_contains',
]);

export const isNegativeComparator = (comparator: ConditionComparator): boolean =>
  NEGATIVE_COMPARATORS.has(comparator);

export type FormFieldConditionForm = {
  source: 'form';
  id: string;
  questionId: string;
  comparator: ConditionComparator;
  value: FormFieldValue;
};

export type FormFieldConditionJourney = {
  source: 'journey';
  id: string;
  variable: FieldMappingFieldItem;
  comparator: ConditionComparator;
  value: FormFieldValue;
  // Server-side bakes the upstream value here so the form runtime can evaluate
  // the condition without re-reading journey state. Undefined in the builder.
  resolvedValue?: FormFieldValue | null;
};

export type FormFieldCondition = FormFieldConditionForm | FormFieldConditionJourney;

// Legacy form conditions persisted before `source` existed have no `source`
// key. The backend treats absent `source` as 'form', so do the same here.
export const isFormSourceCondition = (
  condition: { source?: FormFieldCondition['source'] } | FormFieldCondition
): condition is FormFieldConditionForm => condition.source !== 'journey';

type ConditionAnd = {
  op: 'AND';
  conditions: FormFieldCondition[];
};

type ConditionOr = {
  op: 'OR';
  conditions: FormFieldCondition[];
};

export type FieldType =
  | HeadingFieldType
  | SubheadingFieldType
  | HtmlFieldType
  | ImageFieldType
  | VideoFieldType
  | ContentFileFieldType
  | SeparatorFieldType
  | SectionFieldType
  | SmallInputFieldType
  | LargeInputFieldType
  | KennitalaFieldType
  | NumberFieldType
  | DateFieldType
  | FileFieldType
  | SingleChoiceFieldType
  | MultipleChoiceFieldType
  | DropdownFieldType
  | PhoneFieldType
  | EmailFieldType
  | SubmitFieldType;

export type FieldTypeKind = FieldType['kind'];

export type Condition = ConditionAnd | ConditionOr;

export type FormV2 = {
  schema: 'v2';
  fields: Array<FieldType>;
};

export type FormListsMap = Record<string, FieldOption[]>;

export type NormalizedSectionFieldType = Omit<SectionFieldType, 'fields'> & {
  fields: Array<FieldId>;
};
export type NormalizedFieldType =
  | Exclude<FieldType, { kind: 'section' }>
  | NormalizedSectionFieldType;

export type FieldDefaultProps<T extends FieldTypeKind> = Omit<
  Extract<FieldType, { kind: T }>,
  'id' | 'kind'
>;

export type FieldWithCondition = Extract<FieldType, { condition: Condition }>;
export type InputKind = Extract<FieldTypeKind, `input-${string}`>;
export type ConditionOp = Condition['op'];

export type FormAnswerFieldType =
  | Exclude<
      FieldType,
      {
        kind:
          | 'input-file'
          | 'heading'
          | 'subheading'
          | 'separator'
          | 'html'
          | 'image'
          | 'video'
          | 'content-file'
          | 'submit'
          | 'section';
      }
    >
  | (Omit<FileFieldType, 'value'> & { value?: FormFile[] });

export type FormAnswers = Array<FormAnswerFieldType>;

/**
 * Max length of a short-text field's value, applied both to what a respondent
 * types and to the default a builder sets for them.
 */
export const SHORT_TEXT_MAX_LENGTH = 255;

/** A kennitala is always ten digits. */
export const KENNITALA_LENGTH = 10;
